/**
 * A file trigger's stored `required_inputs`, read as the condition it always
 * was.
 *
 * On a file firing those patterns never selected anything: the runtime mounts
 * the changed file itself and uses the patterns only to decide whether that
 * file fires the trigger at all. That is a condition's job, so a trigger the
 * form opens says it as one and stops sending a gate of its own.
 *
 * Only patterns a condition can state *exactly* are translated. A glob and a
 * SQL `LIKE` do not agree in general -- `*` stops at a `/` and `%` does not --
 * so anything outside the two shapes below is left on `required_inputs`
 * untouched, which keeps the trigger firing on precisely what it did before.
 */

import { type Filter } from "@/shared/domain/query/filters";
import { ACCEPT_ALL_PATTERN } from "@/shared/domain/triggers";

/** The path of the file that fired, as the namespace exposes it. */
const CHANGED_FILE_PATH = "file.relative_path";

/**
 * `**\/*.mcap` -- a file whose name ends in `.mcap`, at any depth. `**\/`
 * matches any leading directories and `*` any basename without a `/`, so every
 * path ending in the suffix matches and no other does: exactly `LIKE '%.mcap'`.
 */
const SUFFIX_GLOB = /^\*\*\/\*(\.[^/*?[\]{}]+)$/;

/**
 * `logs/**` -- everything under `logs/`, which is every path with that prefix:
 * exactly `LIKE 'logs/%'`.
 */
const PREFIX_GLOB = /^([^*?[\]{}]+)\/\*\*$/;

/**
 * The condition row that gates on `patterns`, or null when they cannot be
 * stated as one and must stay where they are.
 *
 * A single pattern only: the runtime accepts a file matching *any* of them,
 * and the form's rows are all required, so two patterns would become a
 * stricter trigger than the one that was saved.
 */
export function gateConditionFor(patterns: readonly string[]): Filter | null {
  if (patterns.length !== 1 || patterns[0] === ACCEPT_ALL_PATTERN) {
    return null;
  }
  const suffix = SUFFIX_GLOB.exec(patterns[0]);
  if (suffix !== null) {
    return likeFilter(`%${escapeLike(suffix[1])}`);
  }
  const prefix = PREFIX_GLOB.exec(patterns[0]);
  if (prefix !== null) {
    return likeFilter(`${escapeLike(prefix[1])}/%`);
  }
  return null;
}

/**
 * The gate every invoke-action target shares, or null when they disagree.
 *
 * `required_inputs` is per target and a condition is per trigger, so the two
 * only line up when the targets ask the same question. Targets that gate
 * differently keep their own patterns.
 */
export function sharedGateOf(
  gates: readonly (readonly string[])[],
): string[] | null {
  if (gates.length === 0) {
    return null;
  }
  const [first, ...rest] = gates;
  const same = rest.every(
    (gate) =>
      gate.length === first.length &&
      gate.every((pattern, index) => pattern === first[index]),
  );
  return same ? [...first] : null;
}

/** A literal in a glob is a literal in the pattern, not a wildcard. */
function escapeLike(literal: string): string {
  return literal.replace(/[\\%_]/g, (character) => `\\${character}`);
}

function likeFilter(value: string): Filter {
  return {
    type: "string",
    field: CHANGED_FILE_PATH,
    comparator: "LIKE",
    values: [value],
  };
}
